import React from "react";
import { Marker, Pane, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { IcMapPhotoPin } from "@racoon/icons";
import { useLang, useT, Namespace } from "@racoon/i18n";
import { Checkbox } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { isPhotoOffSite, PHOTO_OFF_SITE_M, type PhotoPoint } from "../model/helpers/photo-points";
import { useOpenPhotoReport } from "~/features/photo-report-modal/ui/photo-report.modal";

const PHOTO_COLOR = "#007AFF";
const OFF_SITE_COLOR = "#FF9500";
const WIDTH = 18;
const HEIGHT = 22;

// One shared instance: react-leaflet diffs `icon` by reference, and a new one
// per render replaces the marker element and strands an open tooltip.
// The Figma asset is drawn in #151A24; recoloured here so the asset stays as designed.
const photoPinHtml = (width: number, height: number, color = PHOTO_COLOR) =>
  renderToStaticMarkup(React.createElement(IcMapPhotoPin, { width, height })).replace(
    /fill="#151A24"/gi,
    `fill="${color}"`,
  );

// Beside, not under, a site pin at the same point (AIS photos copy the site's
// point): the pickups pane is above this one and would take its hover and click.
const BESIDE_PIN_PX = 16;

const makePhotoIcon = (color: string, className: string, dx = 0) =>
  L.divIcon({
    className,
    html: photoPinHtml(WIDTH, HEIGHT, color),
    iconSize: [WIDTH, HEIGHT],
    // A pin: the position is its tip, not its centre.
    iconAnchor: [WIDTH / 2 - dx, HEIGHT],
  });

const ICONS = {
  onSite: makePhotoIcon(PHOTO_COLOR, "photo-point-marker"),
  offSite: makePhotoIcon(OFF_SITE_COLOR, "photo-point-marker photo-point-off-site"),
  besidePin: makePhotoIcon(PHOTO_COLOR, "photo-point-marker", BESIDE_PIN_PX),
};

// About a metre: a photo this close to the site's point is drawn under its pin.
const SAME_POINT_DEG = 1e-5;
const onSitePin = (point: PhotoPoint) => {
  const site = point.job.pickupLocation;
  return (
    !!site?.lat &&
    !!site?.lon &&
    Math.abs(site.lat - point.lat) < SAME_POINT_DEG &&
    Math.abs(site.lon - point.lon) < SAME_POINT_DEG
  );
};

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

const labelKey = (type: ConnectProto.FileUploadType) => {
  switch (type) {
    case ConnectProto.FileUploadType.JOB_SERVICE_STARTED:
      return "photoMarkerBefore";
    case ConnectProto.FileUploadType.JOB_SERVICE_ENDED:
      return "photoMarkerAfter";
    default:
      return "photoMarkerOther";
  }
};

/** Where the driver stood for each photo of the route drawn. Mount inside a MapContainer. */
export const PhotoPointsLayer: React.FC<{
  points?: PhotoPoint[];
  /** Off inside the photo report itself, where a click would reopen the same report. */
  opensReport?: boolean;
}> = ({ points, opensReport = true }) => {
  const t = useT();
  const { lang } = useLang();
  const timeFormatter = React.useMemo(() => new Intl.DateTimeFormat(lang, TIME_FORMAT), [lang]);
  const formatDistance = React.useMemo(() => {
    const m = new Intl.NumberFormat(lang, {
      style: "unit",
      unit: "meter",
      maximumFractionDigits: 0,
    });
    const km = new Intl.NumberFormat(lang, {
      style: "unit",
      unit: "kilometer",
      maximumFractionDigits: 1,
    });
    return (meters: number) => (meters < 1000 ? m.format(meters) : km.format(meters / 1000));
  }, [lang]);
  const [hovered, setHovered] = React.useState<PhotoPoint | null>(null);
  const openPhotoReport = useOpenPhotoReport();
  const openReport = (job: ConnectProto.Job) => {
    // The modal portals to <body>, which a fullscreen map element would cover.
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    openPhotoReport(job);
  };

  const samePhoto = (a: PhotoPoint, b: PhotoPoint) =>
    a.jobId === b.jobId && a.lat === b.lat && a.lon === b.lon;
  // A marker removed under the cursor (playback scrubbed back) never fires mouseout.
  const hoveredShown = hovered && points?.some((p) => samePhoto(p, hovered)) ? hovered : null;

  if (!points?.length) return null;

  return (
    <>
      {hoveredShown?.site && (
        <Polyline
          positions={[
            [hoveredShown.lat, hoveredShown.lon],
            [hoveredShown.site.lat, hoveredShown.site.lon],
          ]}
          pathOptions={{
            color: isPhotoOffSite(hoveredShown) ? OFF_SITE_COLOR : PHOTO_COLOR,
            weight: 2,
            dashArray: "4 4",
            interactive: false,
          }}
        />
      )}
      {/* Just under the pickups pane (450): a photo beside a site must not take its pin's clicks. */}
      <Pane name="photo-points" style={{ zIndex: 440 }}>
        {points?.map((point) => {
          const offSite = isPhotoOffSite(point);
          const beside = !offSite && onSitePin(point);
          return (
            <Marker
              key={`photo-${point.jobId}-${point.lat}-${point.lon}`}
              position={[point.lat, point.lon]}
              icon={offSite ? ICONS.offSite : beside ? ICONS.besidePin : ICONS.onSite}
              eventHandlers={{
                click: () => opensReport && openReport(point.job),
                mouseover: () => setHovered(point),
                // By position: playback rebuilds a partly revealed point every tick.
                mouseout: () => setHovered((h) => (h && samePhoto(h, point) ? null : h)),
              }}
            >
              {/* Its own pane would put the card under the pickup pins. */}
              <Tooltip
                pane="tooltipPane"
                direction="top"
                offset={[beside ? BESIDE_PIN_PX : 0, -HEIGHT]}
              >
                <div className="text-xs leading-snug">
                  {point.siteName && <div className="font-medium">{point.siteName}</div>}
                  {point.siteDistanceM !== null && (
                    <div style={offSite ? { color: OFF_SITE_COLOR, fontWeight: 500 } : undefined}>
                      {point.siteDistanceM === 0
                        ? t(Namespace.GpsTracking, "photoMarkerOnSite")
                        : t(Namespace.GpsTracking, "photoMarkerFromSite", {
                            distance: formatDistance(point.siteDistanceM),
                          })}
                      {offSite &&
                        ` ${t(Namespace.GpsTracking, "photoMarkerOffSite", {
                          limit: String(PHOTO_OFF_SITE_M),
                        })}`}
                    </div>
                  )}
                  {point.shots.map((shot, idx) => (
                    <div key={idx}>
                      <span style={{ color: PHOTO_COLOR }}>
                        {t(Namespace.GpsTracking, labelKey(shot.type))}
                      </span>
                      {shot.takenAt && <> — {timeFormatter.format(shot.takenAt)}</>}
                    </div>
                  ))}
                  {opensReport && (
                    <div className="mt-1 text-text-secondary">
                      {t(Namespace.GpsTracking, "photoMarkerOpenHint")}
                    </div>
                  )}
                </div>
              </Tooltip>
            </Marker>
          );
        })}
      </Pane>
    </>
  );
};

const legendSwatchHtml = photoPinHtml(10, 12);

/** On/off row for the photo layer, laid out like the GPS stops legend it sits under. */
export const PhotoPointsToggle: React.FC<{
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  points?: PhotoPoint[];
  idPrefix?: string;
}> = ({ enabled, onEnabledChange, points, idPrefix = "photo-points" }) => {
  const t = useT();
  const count = points?.reduce((n, p) => n + p.shots.length, 0) ?? 0;
  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={`${idPrefix}-enabled`}
        checked={enabled}
        onCheckedChange={(checked) => onEnabledChange(!!checked)}
      />
      <span className="flex" dangerouslySetInnerHTML={{ __html: legendSwatchHtml }} />
      <label
        htmlFor={`${idPrefix}-enabled`}
        className="font-medium text-sm cursor-pointer select-none flex-1"
      >
        {t(Namespace.GpsTracking, "photoMarkersTitle")}
      </label>
      <span className="text-xs text-text-secondary tabular-nums">{count}</span>
    </div>
  );
};
